import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import SsoUsers from 'libs/api/ssoUsers';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { useTableChangeFilterHook } from 'hooks/useTableChangeFilterHook';
import { useFilter } from 'hooks/useFilter';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { fileNameEdit } from 'helpers/FileUtils';

import ssoUsersAdapter from './sso-users.adapter';
import Chains from 'libs/api/admin/chains';
import CompaniesApi from 'libs/api/admin/companies';
import ErrorHelper from 'helpers/ErrorHelper';
import LinkWithParams from 'components/linkWithParams';
import { useNavigate } from 'hooks/useNavigate';
import SsoUsersRoles from '../../../../libs/api/ssoUsersRoles';

const api = new SsoUsers();
const adminChainsApi = new Chains();
const companiesApi = new CompaniesApi();
const apiRoles = new SsoUsersRoles();
const apiNotifications = new SsoUsersRoles('notifications');
const apiPermissionModels = new SsoUsersRoles('permission_models');
const apiPermissions = new SsoUsersRoles('permissions');

const defaultState = {
  users: [],
  pagination: {
    current: 1,
    pageSize: 10,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
};

const getState = () => ({
  ...defaultState,
});

export const useSsoUsers = (isMainPage = false) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [usedPremissionIds, setUsedPremissionIds] = useState([]);
  const [usedPremissionApproveIds, setUsedPremissionApproveIds] = useState([]);
  const [premisionsModels, setPremisionsModels] = useState([]);
  const [approveGroups, setApproveGroups] = useState([]);
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const onChangeTable = useTableChangeFilterHook();
  const { handleChangeFilters, handleCloseFilter, searchParams, filterData } =
    useFilter();
  const [chains, setChains] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [acceptsChangePremisions, setAcceptsChangePremisions] = useState(false);
  const [nextRoleId, setNextRoleId] = useState('');
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(getState());
  const [singleItem, setSingleItem] = useState({});
  const [loading, setLoading] = useState(false);
  const [getRolesLodading, setGetRolesLodading] = useState(false);
  const [getCompaniesLoading, setGetCompaniesLoading] = useState(false);
  const [getSingleLoading, setGetSingleLoading] = useState(false);
  const [updateInProcess, setUpdateInProcess] = useState(false);
  const [selectedChain, setSelectedChain] = useState('');
  const [notifications, setNotifications] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        per: data.pagination.pageSize,
        page: data.pagination.current,
        ...Object.fromEntries([...searchParams]),
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const {
        data: { users, pagination },
      } = await api.list({ params });
      setData((s) => ({
        ...s,
        users: [...ssoUsersAdapter(users || [])],
        pagination: {
          ...defaultState.pagination,
          ...pagination,
          total: pagination?.total_count,
        },
      }));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleItem = async () => {
    setGetSingleLoading(true);
    try {
      const { data } = await api.get(`/${id}`);
      setSingleItem(data);

      setSelectedChain(data?.chain?.id);
      let fieldsValues = [];
      for (let key in data.user) {
        if (key !== 'id') {
          fieldsValues.push({
            name: key,
            value: data.user[key],
          });
        }
      }
      fieldsValues.push({
        name: 'shopping_centers',
        value: data.shopping_centers?.map((i) => i.id) || [],
      });
      fieldsValues.push({
        name: 'notification_ids',
        value: data.notifications?.map((i) => i.id) || [],
      });
      fieldsValues.push({
        name: 'user_role_id',
        value: data.user_role.id,
      });

      const prem = [];
      const approve = [];
      data.group_permissions.map((i) => {
        i.permissions.forEach((j, index) => {
          if (index === 0) {
            prem.push({
              ...j,
              mainTitle: i.title,
            });
          } else {
            prem.push(j);
          }
        });
      });
      data.group_approve.map((i) => {
        i.permissions.forEach((j, index) => {
          if (index === 0) {
            approve.push({
              ...j,
              mainTitle: i.title,
            });
          } else {
            approve.push(j);
          }
        });
      });
      fieldsValues.push({
        name: 'permissions_attributes',
        value: prem?.map((i) => ({
          access_type: i.access_type,
          permission_model_ids: i.permission_model_id,
          mainTitle: i.mainTitle,
        })),
      });
      fieldsValues.push({
        name: 'permissions_approve_attributes',
        value: approve?.map((i) => ({
          approve: i.approve,
          permission_model_ids: i.permission_model_id,
          mainTitle: i.mainTitle,
        })),
      });
      if (data.chain) {
        fieldsValues.push({
          name: 'chain_id',
          value: data.chain.id,
        });
        await new Promise((resolve) => setTimeout(resolve, 2500)); // TODO: ask for the logic behind this
        if (data.chain.companies) {
          fieldsValues.push({
            name: 'companies',
            value: data.chain.companies.map((i) => i.id),
          });
        }
      }
      form.setFields(fieldsValues);
    } catch (e) {
      console.log(e);
    } finally {
      setGetSingleLoading(false);
    }
  };

  useEffect(() => {
    if (isMainPage) {
      fetchData();
    }
  }, [searchParams, data.pagination.current, data.sorting.order, isMainPage]);

  const columns = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_center_title',
        width: 150,
        sorter: () => {},
        fixed: 'left',
      },
      {
        title: t('Catena'),
        dataIndex: 'chains.title',
        width: 150,
        sorter: () => {},
      },
      {
        title: t('Cognome'),
        dataIndex: 'surname',
        sorter: () => {},
      },
      {
        title: t('Nome'),
        dataIndex: 'name',
        sorter: () => {},
      },
      {
        title: t('Email'),
        dataIndex: 'email',
        sorter: () => {},
      },
      {
        title: t('Role'),
        dataIndex: 'user_roles.title',
        render: (data) =>
          data
            .split('_')
            .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
            .join(' '),
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 300,
        fixed: 'right',
        render: (data, row) => (
          <Row gutter={16} justify={'end'}>
            {row.can_update && (
              <Col>
                <LinkWithParams to={`${pathname}/new?aditionalId=${row?.id}`}>
                  <Button>{t('Clona')}</Button>
                </LinkWithParams>
              </Col>
            )}
            <Col>
              <LinkWithParams to={`${pathname}/${row.id}`}>
                <Button>{t('APRI')}</Button>
              </LinkWithParams>
            </Col>
            {row.can_update && (
              <Col>
                <LinkWithParams to={`${pathname}/${row.id}/edit`}>
                  <Button type={'secondary'} icon={<EditIcon />} />
                </LinkWithParams>
              </Col>
            )}
            {row.can_destroy && (
              <Col>
                <LinkWithParams to={`${pathname}/${row.id}/delete`}>
                  <Button type={'red'} icon={<DeleteIcon />} />
                </LinkWithParams>
              </Col>
            )}
          </Row>
        ),
      },
    ],
    [pathname, t],
  );

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values:
          filterData?.shopping_centers &&
          filterData?.shopping_centers.map((i) => ({
            value: i.value,
            title: i.title,
          })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values:
          filterData?.companies &&
          filterData?.companies.map((i) => ({
            value: i.value,
            title: i.title,
          })),
        inactiveValues:
          filterData?.inactive_companies &&
          filterData?.inactive_companies.map((i) => ({
            value: i.value,
            title: i.title,
          })),
      },
      {
        title: t('Email'),
        type: 'input',
        name: 'by_email',
      },
      {
        title: t('Cognome'),
        type: 'input',
        name: 'by_surname',
      },
      {
        title: t('Nome'),
        type: 'input',
        name: 'by_name',
      },
      {
        title: t('User ID'),
        type: 'input',
        name: 'by_id',
      },
      {
        title: t('Ruolo'),
        type: 'select',
        name: 'by_user_role',
        values:
          filterData?.roles &&
          filterData?.roles?.map((role) => ({
            value: role.value,
            title: role.title,
          })),
      },
    ],
    [t, filterData],
  );

  const onChangeTableFilter = useCallback(
    (paginate, sort, filter) => {
      onChangeTable(setData, paginate, sort, filter);
    },
    [onChangeTable],
  );

  const onDelete = async (id) => {
    setUpdateInProcess(true);
    try {
      await api.destroy(id);
      navigate(UIHelper.getListRoute(pathname));
      fetchData();
    } catch (e) {
    } finally {
      setUpdateInProcess(false);
    }
  };

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      console.log(e);
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (!isMainPage) {
        try {
          const {
            data: { roles },
          } = await api.get('all_params');
          setRoles(roles);
          const {
            data: { chains = [] },
          } = await adminChainsApi.list({
            params: {
              per: 10000,
            },
          });
          setChains(chains);
        } catch (e) {
          console.log(e);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (selectedChain) {
        setGetCompaniesLoading(true);
        try {
          const { data } = await companiesApi.list({
            params: {
              per: 10000,
              by_chain: selectedChain,
            },
          });

          setCompanies(data.companies);
        } catch (e) {
          console.log(e);
        } finally {
          setGetCompaniesLoading(false);
        }
      }
    })();
  }, [selectedChain]);

  const handleCreateOrUpdate = async (values) => {
    let newValues = { ...values };
    let permissions_attributes = [];
    let permissions_approve_attributes = [];
    values?.permissions_attributes?.forEach((i) => {
      let permission_model_ids = [];
      let premisions = [];
      if (typeof i.permission_model_ids === 'string') {
        permission_model_ids = JSON.parse(i.permission_model_ids);
      } else {
        permission_model_ids = [i.permission_model_ids];
      }
      permission_model_ids.forEach((it) => {
        premisions.push({
          access_type: i.access_type,
          permission_model_id: it,
        });
      });
      permissions_attributes.push(...premisions);
    });
    values?.permissions_approve_attributes?.forEach((i) => {
      let permission_model_ids = [];
      let premisions = [];
      if (typeof i.permission_model_ids === 'string') {
        permission_model_ids = JSON.parse(i.permission_model_ids);
      } else {
        permission_model_ids = [i.permission_model_ids];
      }
      permission_model_ids.forEach((it) => {
        premisions.push({
          approve: i.approve,
          permission_model_id: it,
        });
      });
      permissions_approve_attributes.push(...premisions);
    });
    newValues.permissions_attributes = permissions_attributes;
    newValues.permissions_approve_attributes = permissions_approve_attributes;

    setLoading(true);
    try {
      const data = {
        user: {
          ...newValues,
        },
        companies: newValues.companies || [],
        user_shopping_centers: newValues.shopping_centers || [],
      };
      if (!id) {
        await api.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, { data });
        navigate(UIHelper.getListRoute(pathname, 1));
      }
    } catch (e) {
      console.log(
        'e?.response?.data=',
        (e?.response?.data || [])[0]?.permissions_attributes,
      );
      const err = (e?.response?.data || [])[0]?.permissions_attributes;
      if (err) {
        UIHelper.openNotificationWithIcon('error', err);
      }
      if (e?.response?.data?.length > 0) {
        const companiesErrorText = e?.response?.data[0]?.companies;
        if (companiesErrorText) {
          UIHelper.openNotificationWithIcon('error', companiesErrorText);
        }
      }
      form.setFields(
        ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
          user_companies: 'companies',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!isMainPage && id) {
        await fetchSingleItem();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isMainPage]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { notifications = [] },
        } = await apiNotifications.list({
          params: {
            per: 1000,
          },
        });
        setNotifications(notifications);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  useEffect(async () => {
    if (searchParams.get('aditionalId')) {
      setGetRolesLodading(true);
      try {
        const { data } = await api.get(searchParams.get('aditionalId'));
        let fieldsValues = [];
        fieldsValues.push({
          name: 'notification_ids',
          value: data.notifications?.map((i) => i.id) || [],
        });
        fieldsValues.push({
          name: 'permissions_attributes',
          value: data.permissions?.map((i) => ({
            access_type: i.access_type,
            approve: i.approve,
            permission_model_ids: i.permission_model_id,
          })),
        });
        fieldsValues.push({
          name: 'user_role_id',
          value: data.user_role.id,
        });
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      } finally {
        setGetRolesLodading(false);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { groups = [], approve_groups = [] },
        } = await apiPermissionModels.get('index_group', {
          params: {
            per: 1000,
            sortable_column: 'title',
            direction: 'asc',
          },
        });
        setPremisionsModels(groups);
        setApproveGroups(approve_groups);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const handleDeleteModelById = async (id) => {
    setUpdateInProcess(true);
    try {
      await apiPermissions.destroy(id);
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(async () => {
    if (roleId && acceptsChangePremisions) {
      setGetRolesLodading(true);
      try {
        const {
          data: {
            permissions,
            approve_permissions,
            group_permissions,
            group_approve,
          },
        } = await apiRoles.get(roleId);
        const prem = [];
        const approve = [];
        group_permissions.map((i) => {
          i.permissions.forEach((j, index) => {
            if (index === 0) {
              prem.push({
                ...j,
                mainTitle: i.title,
              });
            } else {
              prem.push(j);
            }
          });
        });
        group_approve.map((i) => {
          i.permissions.forEach((j, index) => {
            if (index === 0) {
              approve.push({
                ...j,
                mainTitle: i.title,
              });
            } else {
              approve.push(j);
            }
          });
        });
        form.setFields([
          {
            name: 'permissions_attributes',
            value: prem?.map((i) => ({
              access_type: i.access_type,
              permission_model_ids: i.permission_model_id,
              mainTitle: i.mainTitle,
            })),
          },
          {
            name: 'permissions_approve_attributes',
            value: approve?.map((i) => ({
              approve: i.approve,
              permission_model_ids: i.permission_model_id,
              mainTitle: i.mainTitle,
            })),
          },
        ]);
      } catch (e) {
        console.log(e);
      } finally {
        setGetRolesLodading(false);
      }
    }
  }, [roleId, acceptsChangePremisions]);

  return {
    data,
    loading,
    updateInProcess,
    columns,
    filters,
    onChangeTableFilter,
    handleChangeFilters,
    handleCloseFilter,
    searchParams,
    handleDownloadExcel,
    onDelete,
    singleItem,
    id,
    navigate,
    pathname,
    chains,
    setSelectedChain,
    companies,
    form,
    roles,
    handleCreateOrUpdate,
    shoppingCenters,
    notifications,
    premisionsModels,
    handleDeleteModelById,
    setRoleId,
    getRolesLodading,
    getCompaniesLoading,
    getSingleLoading,
    setShowRoleModal,
    showRoleModal,
    setAcceptsChangePremisions,
    nextRoleId,
    setNextRoleId,
    approveGroups,
    roleId,
    usedPremissionIds,
    setUsedPremissionIds,
    usedPremissionApproveIds,
    setUsedPremissionApproveIds,
  };
};
